import { useState, useEffect, createContext, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { sendMagicLink, validateClientToken, validateMagicLink } from "@/services/magicLinkService";

interface AuthState {
  isAuthenticated: boolean;
  email: string | null;
  clientId: string | null;
  loading: boolean;
}

interface ClientAuthValue extends AuthState {
  sendMagicLink: (email: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  requiresAuth: boolean;
}

export const ClientAuthContext = createContext<ClientAuthValue | null>(null);

/**
 * Logique interne d'auth client.
 * @param enabled - Quand false, les hooks sont appelés (Rules of Hooks) mais
 *                  l'effet de validation API est court-circuité.
 */
export const useClientAuthInternal = (enabled: boolean = true) => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    email: null,
    clientId: null,
    loading: enabled
  });

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const sendMagicLinkToEmail = async (email: string): Promise<{ success: boolean; message: string }> => {
    if (!clientId) {
      return { success: false, message: "Client ID manquant" };
    }

    if (!isValidEmail(email)) {
      return { success: false, message: "Format d'email invalide" };
    }

    try {
      const result = await sendMagicLink(email, clientId);
      return result;
    } catch (error) {
      console.error("Erreur lors de l'envoi du lien magique:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Erreur lors de l'envoi du lien magique"
      };
    }
  };

  const logout = () => {
    if (clientId) {
      localStorage.removeItem(`client_auth_${clientId}`);
    }
    setAuthState({
      isAuthenticated: false,
      email: null,
      clientId: null,
      loading: false
    });
    navigate("/");
  };

  useEffect(() => {
    // Quand le contexte existe (enabled=false), ne pas lancer de validation API.
    // Les hooks React restent appelés (Rules of Hooks) mais l'effet est inerte.
    if (!enabled) return;

    const checkAuth = async () => {
      if (!clientId) {
        setAuthState({
          isAuthenticated: false,
          email: null,
          clientId: null,
          loading: false
        });
        return;
      }

      const magicToken = searchParams.get('magic_token');
      const email = searchParams.get('email');

      if (magicToken && email) {
        try {
          const result = await validateMagicLink(magicToken, email, clientId);

          if (result.success && result.data?.clientToken) {
            localStorage.setItem(`client_auth_${clientId}`, JSON.stringify({
              email,
              token: result.data.clientToken,
              clientId
            }));

            setAuthState({
              isAuthenticated: true,
              email,
              clientId,
              loading: false
            });

            setSearchParams({});
            return;
          } else {
            console.error('Validation du lien magique échouée:', result.message);
          }
        } catch (error) {
          console.error('Erreur lors de la validation du lien magique:', error);
        }
      }

      try {
        const storedAuth = localStorage.getItem(`client_auth_${clientId}`);

        if (storedAuth) {
          const authData = JSON.parse(storedAuth);

          const tokenValidation = await validateClientToken(authData.token, {
            clientId,
            email: authData.email,
          });

          if (tokenValidation.success && tokenValidation.clientId === clientId) {
            setAuthState({
              isAuthenticated: true,
              email: authData.email,
              clientId: authData.clientId,
              loading: false
            });
          } else if (tokenValidation.isNetworkError) {
            console.warn('Erreur réseau lors de la validation du token, session maintenue');
            setAuthState({
              isAuthenticated: true,
              email: authData.email,
              clientId: authData.clientId,
              loading: false
            });
          } else {
            localStorage.removeItem(`client_auth_${clientId}`);
            setAuthState({
              isAuthenticated: false,
              email: null,
              clientId,
              loading: false
            });
          }
        } else {
          setAuthState({
            isAuthenticated: false,
            email: null,
            clientId,
            loading: false
          });
        }
      } catch (error) {
        console.error("Erreur lors de la vérification de l'authentification:", error);
        setAuthState({
          isAuthenticated: false,
          email: null,
          clientId,
          loading: false
        });
      }
    };

    checkAuth();
  }, [clientId, searchParams, setSearchParams, enabled]);

  return {
    ...authState,
    sendMagicLink: sendMagicLinkToEmail,
    logout,
    requiresAuth: !!clientId && !authState.isAuthenticated && !authState.loading
  };
};

/**
 * Hook public. Lit le contexte partagé si disponible (sous ClientAuthWrapper),
 * sinon fallback sur la logique interne (pages admin/publiques).
 *
 * Les deux hooks sont TOUJOURS appelés (Rules of Hooks), mais useClientAuthInternal
 * est inerte (enabled=false) quand le contexte existe, évitant les appels API dupliqués.
 */
export const useClientAuth = () => {
  const ctx = useContext(ClientAuthContext);
  // Toujours appeler le hook interne (Rules of Hooks), mais désactiver l'effet API
  // quand le contexte est disponible pour éviter les validations token dupliquées.
  const internal = useClientAuthInternal(ctx === null);
  return ctx ?? internal;
};
